/**
 * Execute common app tasks
 *
 * @package stylr
 */
import devlog from './utility/devlog';
import include from './utility/include';
import { endsWith, has, throttle, startsWith } from 'lodash';
import { keycode } from './utility/keycode';
import ModuleLoader from './module-loader';

export default class App {

    /**
     * Create the app
     * @param {object} settings
     */
    constructor( settings = {}) {
        this._onReady = [];
        this._onResize = [];
        this._onScroll = [];
		this.keycode = keycode;
		this.path = '';
		this.includerMap = {};
		this.timeout = 300;
		this.animationSettings = {
			duration: 150,
			ease: 'cubic-bezier(0.55, 0, 0.1, 1)'
		};
        if ( window.app ) {
            for ( let [ name, value ] of Object.entries( window.app ) ) {
                this[name] = value;
            }
		}
		for ( let [ name, value ] of Object.entries( settings ) ) {
			this[name] = value;
		}
		this.moduleLoader = new ModuleLoader( this.map, this.path, this.timeout );
        if ( ! has( this, 'autoInit' ) || this.autoInit ) {
            this.init();
        }
    }

    /**
     * Current screen width
     */
    get width() {
        let inr = window.innerWidth;
        let doc = document.documentElement.clientWidth;
        let bod = document.body.clientWidth;
        return inr || doc || bod;
    }

    /**
     * Current screen height
     */
    get height() {
        let inr = window.innerHeight;
        let doc = document.documentElement.clientHeight;
        let bod = document.body.clientHeight;
        return inr || doc || bod;
    }

    /**
     * Current scroll position
     */
    get scroll() {
        let xOffset = window.pageXOffset;
        let xScroll = document.documentElement.scrollLeft;
        let xClient = document.documentElement.clientLeft;
        let yOffset = window.pageYOffset;
        let yScroll = document.documentElement.scrollTop;
        let yClient = document.documentElement.clientTop;
        return {
            x: ( xOffset || xScroll ) - ( xClient || 0 ),
            y: ( yOffset || yScroll ) - ( yClient || 0 )
        };
	}

	/**
	 * Animate an element
	 * @param {element} el
	 * @param {object} parameters
	 * @param {object} settings
	 */
	animate( el, parameters, settings ) {
		el.animate( parameters, Object.assign({}, this.animationSettings, settings ) );
	}

    /**
     * Include a file or a includer module asynchronously
     * @param {string} name
     */
    include( name ) {
        let loader = null;
        if ( endsWith( name, '.js' ) || endsWith( name, '.css' ) ) {
            loader = include( name );
        } else {
            loader = this.moduleLoader.include( name );
        }
        return loader;
    }

    /**
     * Add registered event listeners
     */
    init() {
        document.addEventListener( 'DOMContentLoaded', () => {
            this._onReady.forEach( func => {
                func();
            });
            window.addEventListener( 'resize', throttle( () => {
                    this._onResize.forEach( func => {
                        func();
                    });
            }, 300 ) );
            window.addEventListener( 'scroll', throttle( () => {
                this._onScroll.forEach( func => {
                    func();
                });
            }, 300 ) );
        });
    }

    /**
     * Register a function to document ready, resize or scroll
     * @param {array|string} events
     * @param {function} func
     */
    on( events, func ) {
        if ( 'string' === typeof events ) {
            events = [ events ];
        }
        events.forEach( event => {
            if ( 'load' === event || 'ready' === event ) {
                devlog(
                    '%cRegistering function to domready event',
                    'color: #ccc'
                );
                this._onReady.push( func );
            }
            if ( 'resize' === event ) {
                devlog(
                    '%cRegistering function to resize event',
                    'color: #ccc'
                );
                this._onResize.push( func );
            }
            if ( 'scroll' === event ) {
                devlog(
                    '%cRegistering function to scroll event',
                    'color: #ccc'
                );
                this._onScroll.push( func );
            }
        });
	}

	/**
	 * Prepend filename with project path
	 * @param {string} file
	 */
	path( file ) {
		if ( startsWith( file, '/' ) || startsWith( file, 'http' ) ) {
			return file;
		}
		return  this.path + file;
	}
}
